import { getInspirationGalleryUploadProgressUrl } from "./inspiration-gallery-upload-progress-url";
import {
    addPendingInspirationUploadId,
    clearInspirationBulkSnapshot,
    getPendingInspirationUploadIds,
    incrementInspirationBulkSnapshotCompleted,
    removePendingInspirationUploadId,
} from "./inspiration-gallery-upload-session";
import {
    completeBulkGalleryItem,
    endBulkGalleryJob,
    finishUpload,
    removeUpload,
    startUpload,
    updateBulkGalleryZipProgress,
    updateProgress,
} from "../store/uploadProgress/uploadProgressSlice";
import { clearGalleryList, getInspirationGalleryData } from "../store/inspirationGallery/inspiration-gallery-slice";

/** One open EventSource per uploadId */
const eventSourceByUploadId = new Map();

/** Same uploadId: one in-flight promise (e.g. React Strict Mode). */
const promiseByUploadId = new Map();

/** User closed the progress UI; stop reconnect loops and reject in-flight waitFor. */
const userDismissedUploadIds = new Set();

export const INSPIRATION_GALLERY_PROGRESS_DISMISSED_MESSAGE = "DISMISSED";

/**
 * Dismiss bottom progress: closes SSE, clears session snapshot for bulk, and rejects active waitFor with DISMISSED.
 */
export function dismissInspirationGalleryProgressFromUi(dispatch, { type, uploadId } = {}) {
    if (type === "bulk") {
        const fromPending = getPendingInspirationUploadIds();
        const fromMap = [...eventSourceByUploadId.keys()];
        const allIds = [...new Set([...fromPending, ...fromMap])];
        allIds.forEach((id) => userDismissedUploadIds.add(id));
        for (const [, es] of [...eventSourceByUploadId.entries()]) {
            try {
                es.close();
            } catch {
                /* ignore */
            }
        }
        dispatch(endBulkGalleryJob());
        clearInspirationBulkSnapshot();
        allIds.forEach((id) => removePendingInspirationUploadId(id));
        return;
    }
    if (type === "single" && uploadId) {
        userDismissedUploadIds.add(uploadId);
        const es = eventSourceByUploadId.get(uploadId);
        if (es) {
            try {
                es.close();
            } catch {
                /* ignore */
            }
        } else if (!promiseByUploadId.has(uploadId)) {
            userDismissedUploadIds.delete(uploadId);
            removePendingInspirationUploadId(uploadId);
            dispatch(removeUpload({ id: uploadId }));
        }
    }
}

const MAX_SSE_ATTEMPTS = 18;
const RETRY_MS = 2200;
/** From SSE “done” until the single-upload progress bar is removed (user sees 1→100 from server, then ~5s at 100%). */
const SINGLE_UPLOAD_BAR_DISMISS_MS = 5000;
/** Refresh list + resolve promise shortly after done so toast can show; bar stays until SINGLE_UPLOAD_BAR_DISMISS_MS. */
const SINGLE_UPLOAD_POST_DONE_TICK_MS = 400;

/**
 * Subscribe to gallery processing SSE until "done" or repeated failure.
 * Does not clear sessionStorage on transient errors (tab refresh closes the old
 * EventSource and fires onerror — we must keep the id so the next page can restore).
 */
export function waitForInspirationGalleryUploadDone(uploadId, dispatch, options = {}) {
    const {
        refreshListOnDone = true,
        persistSession = true,
        bulkGalleryMode = false,
    } = options;

    const settleAfterDoneMs = bulkGalleryMode ? 350 : SINGLE_UPLOAD_POST_DONE_TICK_MS;

    const existing = promiseByUploadId.get(uploadId);
    if (existing) {
        /* Strict Mode / remount: Redux resets but the same promise may still be in flight */
        if (!bulkGalleryMode) {
            dispatch(startUpload({ id: uploadId }));
        }
        return existing;
    }

    if (persistSession) {
        addPendingInspirationUploadId(uploadId);
    }

    if (!bulkGalleryMode) {
        dispatch(startUpload({ id: uploadId }));
    }

    const prev = eventSourceByUploadId.get(uploadId);
    if (prev) {
        prev.close();
        eventSourceByUploadId.delete(uploadId);
    }

    const url = getInspirationGalleryUploadProgressUrl(uploadId);

    const promise = new Promise((resolve, reject) => {
        let settled = false;
        let lastPercent = 0;
        let attempt = 0;
        let retryTimer = null;
        let activeEs = null;

        const forceDismissedSettle = () => {
            if (settled) return;
            userDismissedUploadIds.delete(uploadId);
            settled = true;
            promiseByUploadId.delete(uploadId);
            if (retryTimer != null) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
            if (activeEs) {
                activeEs.close();
                if (eventSourceByUploadId.get(uploadId) === activeEs) {
                    eventSourceByUploadId.delete(uploadId);
                }
                activeEs = null;
            }
            removePendingInspirationUploadId(uploadId);
            if (bulkGalleryMode) {
                dispatch(endBulkGalleryJob());
                clearInspirationBulkSnapshot();
            } else {
                dispatch(removeUpload({ id: uploadId }));
            }
            reject(new Error(INSPIRATION_GALLERY_PROGRESS_DISMISSED_MESSAGE));
        };

        const abortConnections = () => {
            if (retryTimer != null) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
            if (activeEs) {
                activeEs.close();
                if (eventSourceByUploadId.get(uploadId) === activeEs) {
                    eventSourceByUploadId.delete(uploadId);
                }
                activeEs = null;
            }
        };

        const failPermanent = () => {
            if (settled) return;
            settled = true;
            promiseByUploadId.delete(uploadId);
            abortConnections();
            removePendingInspirationUploadId(uploadId);
            if (bulkGalleryMode) {
                dispatch(endBulkGalleryJob());
                clearInspirationBulkSnapshot();
            } else {
                dispatch(removeUpload({ id: uploadId }));
            }
            reject(new Error("Upload progress connection failed"));
        };

        const attach = () => {
            if (settled) return;
            if (userDismissedUploadIds.has(uploadId)) {
                forceDismissedSettle();
                return;
            }

            if (retryTimer != null) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }

            attempt += 1;
            if (attempt > MAX_SSE_ATTEMPTS) {
                failPermanent();
                return;
            }

            if (activeEs) {
                activeEs.close();
                if (eventSourceByUploadId.get(uploadId) === activeEs) {
                    eventSourceByUploadId.delete(uploadId);
                }
                activeEs = null;
            }

            let es;
            try {
                es = new EventSource(url);
            } catch {
                retryTimer = setTimeout(attach, RETRY_MS);
                return;
            }

            activeEs = es;
            eventSourceByUploadId.set(uploadId, es);

            es.addEventListener("progress", (e) => {
                if (settled) return;
                const d = JSON.parse(e.data);
                lastPercent = typeof d.percent === "number" ? d.percent : lastPercent;
                if (bulkGalleryMode) {
                    dispatch(
                        updateBulkGalleryZipProgress({
                            percent: d.percent,
                            currentFile: d.currentFile,
                        })
                    );
                } else {
                    dispatch(
                        updateProgress({
                            id: uploadId,
                            percent: d.percent,
                            currentFile: d.currentFile,
                        })
                    );
                }
            });

            es.addEventListener("done", () => {
                if (settled) return;
                settled = true;
                /**
                 * Drop in-flight dedupe immediately so the next bulk item (or a duplicate
                 * uploadId from the API) always opens a new EventSource instead of awaiting
                 * an already-settled promise.
                 */
                promiseByUploadId.delete(uploadId);
                abortConnections();
                removePendingInspirationUploadId(uploadId);
                if (bulkGalleryMode) {
                    dispatch(completeBulkGalleryItem());
                    incrementInspirationBulkSnapshotCompleted();
                    setTimeout(() => {
                        if (refreshListOnDone) {
                            dispatch(clearGalleryList());
                            dispatch(getInspirationGalleryData({ skip: 0, limit: 24 }));
                        }
                        resolve();
                    }, settleAfterDoneMs);
                } else {
                    dispatch(finishUpload({ id: uploadId }));
                    setTimeout(() => {
                        if (refreshListOnDone) {
                            dispatch(clearGalleryList());
                            dispatch(getInspirationGalleryData({ skip: 0, limit: 24 }));
                        }
                        resolve();
                    }, settleAfterDoneMs);
                    setTimeout(() => {
                        dispatch(removeUpload({ id: uploadId }));
                    }, SINGLE_UPLOAD_BAR_DISMISS_MS);
                }
            });

            es.onerror = () => {
                if (settled) return;
                if (userDismissedUploadIds.has(uploadId)) {
                    if (retryTimer != null) {
                        clearTimeout(retryTimer);
                        retryTimer = null;
                    }
                    es.close();
                    if (eventSourceByUploadId.get(uploadId) === es) {
                        eventSourceByUploadId.delete(uploadId);
                    }
                    if (activeEs === es) {
                        activeEs = null;
                    }
                    forceDismissedSettle();
                    return;
                }
                es.close();
                if (eventSourceByUploadId.get(uploadId) === es) {
                    eventSourceByUploadId.delete(uploadId);
                }
                if (activeEs === es) {
                    activeEs = null;
                }
                /**
                 * Do not call removePendingInspirationUploadId here.
                 * Refresh / navigation closes the stream and fires onerror; the next load
                 * must still see the pending id and reopen SSE.
                 */
                if (bulkGalleryMode) {
                    dispatch(
                        updateBulkGalleryZipProgress({
                            percent: lastPercent,
                            currentFile: "Reconnecting…",
                        })
                    );
                } else {
                    dispatch(
                        updateProgress({
                            id: uploadId,
                            percent: lastPercent,
                            currentFile: "Reconnecting…",
                        })
                    );
                }
                if (retryTimer != null) clearTimeout(retryTimer);
                retryTimer = setTimeout(attach, RETRY_MS);
            };
        };

        attach();
    });

    promiseByUploadId.set(uploadId, promise);
    promise.finally(() => {
        promiseByUploadId.delete(uploadId);
    });

    return promise;
}
